import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// Onboarding's copy (S10.04, S11.01): the profile, then the first analysis, one key per string in
// English and Spanish. `useOnboardingCopy` (behavior/use-onboarding-copy.ts) reads the active
// language. A `{name}` in a line is filled in by `t(key, { name })`.

export type OnboardingCatalog = typeof EN;

const EN = {
  // The profile: the first screen, and /complete-profile later.
  profileTitle: "Complete your profile",
  profileSubtitle: "Just a few details before you get started.",
  firstName: "First name",
  lastName: "Last name",
  firstNamePlaceholder: "Ada",
  lastNamePlaceholder: "Lovelace",
  phone: "Phone number",
  phoneWhy: "Only to help you get set up, and to keep the platform free of spam.",
  website: "Business website",
  websitePlaceholder: "mybusiness.com",
  saving: "Saving…",
  continue: "Continue",
  save: "Save",
  saveDetailsFailed: "We couldn't save your details",
  // The Terms box (ADR-032): on the profile for a new person, on its own for anyone else.
  termsAgreeLead: "I agree to the",
  termsLink: "Terms of Service",
  termsAgreeJoin: "and have read the",
  privacyLink: "Privacy Policy",
  termsAgreeEnd: ".",
  termsAgreeLabel: "I agree to the Terms of Service and have read the Privacy Policy.",
  termsGateTitle: "Accept our Terms to continue",
  termsUpdatedTitle: "Our Terms have changed",
  termsGateSubtitle: "Read them, then tick the box to keep going.",
  acceptTermsFailed: "We couldn't record your acceptance",
  // The way out of the gate (S37.07): nobody is held here.
  termsExitTitle: "Would you rather not accept?",
  termsExitSignOut: "Sign out",
  termsExitSignOutFailed: "We couldn't sign you out",
  termsExitDataCopy: "For a copy of your data, write to {address}.",
  // A staff visit at the gate (ADR-032 point 6): only the person can accept.
  termsVisitNotice: "You are viewing as this person. Only they can accept the Terms.",
  termsVisitStop: "Stop viewing",
  termsVisitStopFailed: "We couldn't stop this visit",
  // The first analysis: the second screen.
  analysisTitle: "Find your first leads",
  analysisSubtitle: {
    one: "You have 1 free credit. Try it now on a first analysis.",
    other: "You have {count} free credits. Try them now on a first analysis.",
  },
  analysisSubtitleNoCount: "You have free credits. Try them now on a first analysis.",
  analysisSubtitleEmphasis: { one: "1 free credit", other: "{count} free credits" },
  analysisSubtitleNoCountEmphasis: "free credits",
  analysisRunning: "Your analysis is running on your free credits. More leads keep arriving.",
  welcomeWithheldTitle: "No free credits on this account",
  welcomeWithheldDescription:
    "Free credits are given once per person, and this account didn't receive them. Buy a credit pack or choose a plan to keep going.",
  buyCredits: "Buy credits",
  seePlans: "See plans",
  where: "Where",
  whatToFind: "What to look for",
  instagramDescription: "An account's followers",
  xDescription: "Followers, replies and keywords",
  /** Set against a platform no real provider reads yet. */
  comingSoon: "Coming soon",
  mapsDescription: "Local businesses by type and area",
  tiktokDescription: "Followers, comments and creators by niche",
  linkedinDescription: "People and companies by role, industry and page",
  sourceFollowers: "Followers",
  sourceFollowersDescription: "People who follow an account",
  sourceLocation: "Location",
  sourceLocationDescription: "People posting from a place",
  sourceHashtag: "Hashtag",
  sourceHashtagDescription: "People posting with a hashtag",
  sourceComments: "Comments",
  sourceCommentsDescription: "People who comment on an account's posts",
  sourceLikes: "Likes",
  sourceLikesDescription: "People who like an account's posts",
  sourceReplies: "Replies",
  sourceRepliesDescription: "People who reply to an account",
  sourceKeyword: "Keyword",
  sourceKeywordDescription: "People posting about a topic",
  account: "Account",
  accountPlaceholder: "@nike",
  place: "Place",
  placePlaceholder: "Madrid",
  hashtag: "Hashtag",
  hashtagPlaceholder: "#running",
  keyword: "Keyword",
  keywordPlaceholder: "running shoes",
  businessKind: "Kind of business",
  businessKindPlaceholder: "Dentists",
  area: "Area",
  areaPlaceholder: "Madrid",
  findLeads: "Find leads",
  starting: "Starting…",
  startFailed: "We couldn't start the analysis",
  analysisProgress: "Analysis progress",
  profiles: "Profiles",
  emails: "Emails",
  phones: "Phones",
  firstLeads: "Your first leads",
  lookingForLeads: "Looking for leads…",
  seeLeads: "See all my leads",
  goToDashboard: "Go to the dashboard",
  leadsStillArriving: "Leads still arriving…",
  later: "Do this later and explore the platform",
  finishFailed: "Could not finish onboarding",

  // Document titles.
  screenTitleGetStarted: "Get started · {product}",
  screenTitleProfile: "Your profile · {product}",
} as const;

const ES: Translation<OnboardingCatalog> = {
  profileTitle: "Completa tu perfil",
  profileSubtitle: "Solo unos datos antes de empezar.",
  firstName: "Nombre",
  lastName: "Apellido",
  firstNamePlaceholder: "Ada",
  lastNamePlaceholder: "Lovelace",
  phone: "Número de teléfono",
  phoneWhy: "Solo para ayudarte a empezar y mantener la plataforma libre de spam.",
  website: "Sitio web de tu negocio",
  websitePlaceholder: "minegocio.com",
  saving: "Guardando…",
  continue: "Continuar",
  save: "Guardar",
  saveDetailsFailed: "No pudimos guardar tus datos",
  termsAgreeLead: "Acepto los",
  termsLink: "Términos del servicio",
  termsAgreeJoin: "y he leído la",
  privacyLink: "Política de privacidad",
  termsAgreeEnd: ".",
  termsAgreeLabel: "Acepto los Términos del servicio y he leído la Política de privacidad.",
  termsGateTitle: "Acepta nuestros Términos para continuar",
  termsUpdatedTitle: "Nuestros Términos han cambiado",
  termsGateSubtitle: "Léelos y marca la casilla para seguir.",
  acceptTermsFailed: "No pudimos registrar tu aceptación",
  termsExitTitle: "¿Prefieres no aceptarlos?",
  termsExitSignOut: "Cerrar sesión",
  termsExitSignOutFailed: "No pudimos cerrar tu sesión",
  termsExitDataCopy: "Para recibir una copia de tus datos, escribe a {address}.",
  termsVisitNotice: "Estás viendo como esta persona. Solo ella puede aceptar los Términos.",
  termsVisitStop: "Dejar de ver",
  termsVisitStopFailed: "No pudimos terminar esta visita",
  analysisTitle: "Encuentra tus primeros leads",
  analysisSubtitle: {
    one: "Tienes 1 crédito gratis. Pruébalo ahora con un primer análisis.",
    other: "Tienes {count} créditos gratis. Pruébalos ahora con un primer análisis.",
  },
  analysisSubtitleNoCount: "Tienes créditos gratis. Pruébalos ahora con un primer análisis.",
  analysisSubtitleEmphasis: { one: "1 crédito gratis", other: "{count} créditos gratis" },
  analysisSubtitleNoCountEmphasis: "créditos gratis",
  analysisRunning: "Tu análisis está en marcha con tus créditos gratis. Siguen llegando más leads.",
  welcomeWithheldTitle: "Esta cuenta no tiene créditos gratis",
  welcomeWithheldDescription:
    "Los créditos gratis se entregan una vez por persona, y esta cuenta no los recibió. Compra un paquete de créditos o elige un plan para seguir adelante.",
  buyCredits: "Comprar créditos",
  seePlans: "Ver planes",
  where: "Dónde",
  whatToFind: "Qué buscar",
  instagramDescription: "Los seguidores de una cuenta",
  xDescription: "Seguidores, respuestas y palabras clave",
  comingSoon: "Próximamente",
  mapsDescription: "Negocios locales por tipo y zona",
  tiktokDescription: "Seguidores, comentarios y creadores por nicho",
  linkedinDescription: "Personas y empresas por cargo, sector y página",
  sourceFollowers: "Seguidores",
  sourceFollowersDescription: "Quienes siguen una cuenta",
  sourceLocation: "Ubicación",
  sourceLocationDescription: "Quienes publican desde un lugar",
  sourceHashtag: "Hashtag",
  sourceHashtagDescription: "Quienes publican con un hashtag",
  sourceComments: "Comentarios",
  sourceCommentsDescription: "Quienes comentan las publicaciones de una cuenta",
  sourceLikes: "Me gusta",
  sourceLikesDescription: "Quienes dan me gusta a las publicaciones de una cuenta",
  sourceReplies: "Respuestas",
  sourceRepliesDescription: "Quienes responden a una cuenta",
  sourceKeyword: "Palabra clave",
  sourceKeywordDescription: "Quienes publican sobre un tema",
  account: "Cuenta",
  accountPlaceholder: "@nike",
  place: "Lugar",
  placePlaceholder: "Madrid",
  hashtag: "Hashtag",
  hashtagPlaceholder: "#running",
  keyword: "Palabra clave",
  keywordPlaceholder: "zapatillas para correr",
  businessKind: "Tipo de negocio",
  businessKindPlaceholder: "Dentistas",
  area: "Zona",
  areaPlaceholder: "Madrid",
  findLeads: "Buscar leads",
  starting: "Iniciando…",
  startFailed: "No pudimos iniciar el análisis",
  analysisProgress: "Progreso del análisis",
  profiles: "Perfiles",
  emails: "Correos",
  phones: "Teléfonos",
  firstLeads: "Tus primeros leads",
  lookingForLeads: "Buscando leads…",
  seeLeads: "Ver todos mis leads",
  goToDashboard: "Ir al panel",
  leadsStillArriving: "Siguen llegando leads…",
  later: "Hacerlo más tarde y explorar la plataforma",
  finishFailed: "No pudimos terminar la configuración",
  screenTitleGetStarted: "Empieza · {product}",
  screenTitleProfile: "Tu perfil · {product}",
};

export const ONBOARDING_CATALOGS: Catalogs<OnboardingCatalog> = { en: EN, es: ES };
